import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api.js';

/*
 * A league's dashboard.
 *
 * Two shapes come back from POST /api/standings. A non-FPL league returns
 * `nonFplLeague: true` with a simple standings table; an FPL league returns
 * the tabbed payload — managers, selections, captains, chips, past seasons,
 * all-time ranks and the bar chart race — all in one response.
 *
 * Some sections are optional and the tabs for them only appear when the
 * data does, matching the ng-show flags on the old template.
 */
export function useLeague(div) {
	const [config, setConfig] = useState(null);
	const [data, setData] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);
	const [refreshing, setRefreshing] = useState(false);

	const load = useCallback(
		async (leagueConfig, { isRefresh = false } = {}) => {
			if (isRefresh) setRefreshing(true);
			else setStatus('loading');
			setError(null);
			try {
				const payload = await api.post('/api/standings', {
					league_name: leagueConfig.league_name,
					league_type: leagueConfig.league_type,
				});
				/* A queued response arrives wrapped; a direct one does not. */
				setData(payload?.result ?? payload);
				setStatus('ready');
			} catch (err) {
				setError(err instanceof ApiError ? err.message : 'Could not load the league.');
				setStatus('error');
			} finally {
				setRefreshing(false);
			}
		},
		[],
	);

	useEffect(() => {
		let cancelled = false;
		setStatus('loading');
		api
			.get(`/api/league/${div}`)
			.then((cfg) => {
				if (cancelled) return;
				if (!cfg || cfg.result === 'ERROR') {
					setError(`No league named ${div}.`);
					setStatus('missing');
					return;
				}
				setConfig(cfg);
				return load(cfg);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load the league.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [div, load]);

	const refresh = useCallback(() => {
		if (config) load(config, { isRefresh: true });
	}, [config, load]);

	return { config, data, status, error, refreshing, refresh };
}

/*
 * Past seasons are paged by stepping the season label, since the API takes
 * a season rather than an offset. "2024/25" -> "2023-24".
 */
export function stepSeason(label, direction) {
	const [start, end] = label.split('/');
	const delta = direction === 'prev' ? -1 : 1;
	const nextStart = Number(start) + delta;
	const nextEnd = Number(end) + delta;
	return `${nextStart}-${String(nextEnd).padStart(2, '0')}`;
}

export function usePastSeasons(leagueName, initialRows, initialLabel) {
	const [rows, setRows] = useState(initialRows ?? []);
	const [label, setLabel] = useState(initialLabel ?? null);
	const [loading, setLoading] = useState(false);
	const [note, setNote] = useState(null);

	useEffect(() => {
		setRows(initialRows ?? []);
		setLabel(initialLabel ?? null);
	}, [initialRows, initialLabel]);

	const go = useCallback(
		async (direction) => {
			if (!label || !leagueName) return;
			const target = stepSeason(label, direction);
			setLoading(true);
			setNote(null);
			try {
				const next = await api.get(`/api/league/${leagueName}/history/${target}`);
				if (!next?.length) {
					/* The old page raised an alert here; an inline note says the
					 * same thing without interrupting. */
					setNote(`No data recorded for ${target.replace('-', '/')}.`);
					return;
				}
				setRows(next);
				setLabel(target.replace('-', '/'));
			} catch {
				setNote('Could not load that season.');
			} finally {
				setLoading(false);
			}
		},
		[label, leagueName],
	);

	return { rows, label, loading, note, go };
}
